<script setup lang="ts">
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import { WidgetSeoContent } from '~/components/widget/seo'
import {
  WidgetBankingStatsHeader,
  WidgetBankingStatsKpis,
  WidgetBankingStatsRates,
  WidgetBankingStatsFacts,
  WidgetBankingStatsRegions,
  WidgetBankingStatsCurrency,
  WidgetBankingStatsSectors,
  WidgetBankingStatsDepositors,
  WidgetBankingStatsLiquidity
} from '~/components/widget/banking-statistics'
import type {
  BankingFactBlock,
  IBankingSummaryResponse
} from '~/components/widget/banking-statistics'

const { t } = useI18n()

// Awaited so the KPI cards are part of the server-rendered HTML.
const { data: response } = await useApi<IBankingSummaryResponse>('/banking-statistics/summary', {
  key: 'banking-statistics-summary'
})

const summary = computed(() => response.value?.data ?? null)

/** Sections with an anchor on this page; facts about other blocks are shown without a link. */
const RENDERED_BLOCKS: readonly BankingFactBlock[] = [
  'summary',
  'rates',
  'regions',
  'currency',
  'sectors',
  'depositors',
  'liquidity'
]

const breadcrumbs = [
  { title: t('common.banks'), url: '/banks' },
  { title: t('common.statistics'), url: '/banks/statistics' }
]

// SEO settings from the CMS set the title in app.vue; the page's own title is only a fallback.
const nuxtApp = useNuxtApp()
const hasSeoTitle = computed(() =>
  Boolean(nuxtApp._seoData?.value?.meta_title || nuxtApp._seoData?.value?.title)
)

useHead(() =>
  hasSeoTitle.value
    ? {}
    : { title: `${t('banking_statistics.title')} ${t('seo.suffix.banks_kyrgyzstan')}` }
)
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs :links="breadcrumbs" />
      <div class="space-y-5">
        <!-- The header is the hero: the only h1 plus the SEO text from the CMS -->
        <WidgetBankingStatsHeader :summary="summary" />
        <WidgetBankingStatsKpis :kpis="summary?.kpis ?? []" :as-of="summary?.as_of ?? null" />
        <WidgetBankingStatsRates v-if="summary?.as_of" :as-of="summary.as_of" />
        <WidgetBankingStatsFacts :facts="summary?.facts ?? []" :blocks="RENDERED_BLOCKS" />
        <template v-if="summary?.as_of">
          <WidgetBankingStatsRegions />
          <WidgetBankingStatsCurrency :facts="summary.facts" />
          <WidgetBankingStatsSectors />
          <WidgetBankingStatsDepositors />
          <WidgetBankingStatsLiquidity />
        </template>

        <WidgetSeoContent position="bottom" />
      </div>
    </Container>
  </Main>
</template>
